<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Summoner Necromancer Build Guide</h1>
    <p>The Summoner Necromancer is one of the safest and most stable builds in Diablo 2: Resurrected. By summoning skeleton warriors, mages, and other minions, they take care of most of the fighting while you focus on casting curses, supporting, and controlling the battlefield. This build is excellent for farming bosses, clearing maps, and even tackling difficult areas.</p>

    <div>
      <img class="build-screenshot" alt="build screenshot" src="@/assets/builds/bd-summon-nec.png">
    </div>
    
    <div class="section">
        <h2>Recommended Gear</h2>
        <Equipments
          class="right"
          :left-weapon="weapon"
          :right-weapon="shield"
          :helm="helm"
          :armor="armor"
          :gloves="gloves"
          :belt="belt"
        />
        <h3>Weapon</h3>
        <ul>
            <li>Beast Runeword – Provides Fanaticism Aura, increasing skeleton attack speed and damage</li>
            <li>Arm of King Leoric – High bonuses to summoning skills</li>
        </ul>
        <h3>Shield</h3>
        <ul>
            <li>Spirit Monarch – FCR, mana, and skills</li>
            <li>Homunculus – High resistances and skill bonuses</li>
        </ul>
        <h3>Helmet</h3>
        <ul>
            <li>Harlequin Crest (Shako) – +2 Skills, high life and mana</li>
        </ul>
        <h3>Armor</h3>
        <ul>
            <li>Enigma – Grants Teleport, making positioning much easier</li>
        </ul>
        <h3>Belt</h3>
        <ul>
            <li>Arachnid Mesh – +1 Skills and Faster Cast Rate</li>
            <li>Trang-Oul’s Girth – Mana sustain and resistances</li>
        </ul>
        <h3>Gloves</h3>
        <ul>
            <li>Trang-Oul’s Claws – FCR and poison bonuses</li>
            <li>Magefist – FCR and mana regeneration</li>
        </ul>
        <h3>Rings</h3>
        <ul>
            <li>Stone of Jordan – +1 Skills, increased mana</li>
            <li>Bul-Kathos' Wedding Band – +1 Skills (If you don't have SOJ)</li>
            <li>If FCR is below 75%, use a rare ring with 10% FCR</li>
        </ul>
        <h3>Amulet</h3>
        <ul>
            <li>Mara’s Kaleidoscope – +2 Skills and high resistances</li>
            <li>A +3 Summoning Skills amulet or +2 Necromancer Skills crafted amulet</li>
        </ul>
    </div>

    <div id="slot-content_mpu" class="content-ad-mpu"/>

    <div class="section">
        <h2>Skill Allocation</h2>
        <div class="m-3 skills-tab right">
          <b-tabs small content-class="m-2" class="tab-text">
            <b-tab title="Summoning">
              <img class="w-100" src="@/assets/builds/skills-summon-nec-1.png">
            </b-tab>
            <b-tab title="Poison & Bone" active>
              <img class="w-100" src="@/assets/builds/skills-summon-nec-2.png">
            </b-tab>
            <b-tab title="Curses">
              <img class="w-100" src="@/assets/builds/skills-summon-nec-3.png">
            </b-tab>
          </b-tabs>
        </div>
        <h3>Core Skills (Max 20 Points Each)</h3>
        <ul>
            <li>Raise Skeleton – Main damage source and tank</li>
            <li>Skeleton Mastery – Enhances skeleton damage and health</li>
            <li>Corpse Explosion – Powerful AOE damage to clear the battlefield</li>
        </ul>
        <h3>Support Skills</h3>
        <ul>
            <li>Summon Resist – 1 point, increases elemental resistance of minions</li>
            <li>Raise Skeletal Mage – 1 point, provides additional ranged elemental damage</li>
            <li>Clay Golem – 1 point, slows enemies and helps in boss fights</li>
            <li>Golem Mastery – 1 point, boosts golem survivability</li>
            <li>Revive – Several points, summons strong temporary minions</li>
        </ul>
        <h3>Curses</h3>
        <ul>
            <li>Decrepify – 1 point, slows and weakens enemies, great for bosses</li>
            <li>Weaken – 1 point, reduces enemy damage</li>
            <li>Amplify Damage – 1 point, greatly increases physical damage taken by enemies</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Stat Distribution</h2>
        <ul>
            <li><strong>Strength:</strong> Just enough for gear (typically 50-100)</li>
            <li><strong>Dexterity:</strong> Minimal or enough for gear requirements</li>
            <li><strong>Vitality:</strong> All remaining points for survivability</li>
            <li><strong>Energy:</strong> No points, rely on gear and mana potions</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Mercenary Selection</h2>
        <h3>Recommended: Act 2 Might Aura Mercenary</h3>
        <ul>
            <li><strong>Weapon:</strong> Pride Runeword – Provides Concentration Aura, boosting skeleton damage</li>
            <li><strong>Helmet:</strong> Andariel’s Visage – Increased attack speed and life leech</li>
            <li><strong>Armor:</strong> Fortitude – High resistances and survivability boost</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Gameplay Strategy</h2>
        <ul>
            <li>Summon skeleton warriors first to ensure a strong front line.</li>
            <li>Use <strong>Amplify Damage</strong> to increase physical damage output or <strong>Decrepify</strong> against bosses.</li>
            <li>Once enemies start dying, <strong>Corpse Explosion</strong> will quickly clear the battlefield.</li>
            <li>Use <strong>Enigma's Teleport</strong> to reposition your army as needed.</li>
        </ul>
    </div>
    
    <p>The Summoner Necromancer is a low-effort, high-survivability build that is excellent for farming, boss fights, and group play. With strong minions and powerful corpse-based attacks, it is one of the most effective PvE builds in Diablo 2: Resurrected.</p>

  </div>
</template>

<script>
import BlogList from '../BlogList.vue'
import Equipments from './Equipments.vue'

export default {
  name: 'SummonNec',

  components: {
    BlogList,
    Equipments,
  },

  computed: {
    weapon () {
      return {
        'img': 'eq-war-axe',
        'width': 80,
        'title': 'Beast Runeword',
        'runes': 'Ber + Tir + Um + Mal + Lum',
        'detail': [
          'Level 9 Fanaticism Aura When Equipped',
          '+40% Increased Attack Speed',
          '+240-270% Enhanced Damage',
          "20% Chance of Crushing Blow",
          '25% Chance of Open Wounds',
          '+3 To Werebear',
          '+3 To Lycanthropy',
          'Prevent Monster Heal',
          '+25-40 To Strength',
          '+10 To Energy',
          '+2 To Mana After Each Kill',
          'Level 13 Summon Grizzly (5 Charges)'
        ],
      }
    },

    shield () {
      return {
        'img': 'eq-monarch',
        'width': 80,
        'title': 'Spirit Runeword',
        'runes': 'Tal + Thul + Ort + Amn',
        'detail': [
          '+2 To All Skills', 
          '+25-35% Faster Cast Rate',
          '+55% Faster Hit Recovery',
          '+250 Defense Vs. Missile', 
          '+22 To Vitality',
          '+89-112 To Mana',
          'Cold Resist +35%',
          'Lightning Resist +35%',
          'Poison Resist +35%',
          '+3-8 Magic Absorb',
          'Attacker Takes Damage of 14',
        ],
      }
    },

    helm () {
      return {
        'img': 'eq-shako',
        'width': 75,
        'title': 'Harlequin Crest',
        'detail': [
          '+2 To All Skills', 
          '1-148 To Life (Based On Character Level)',
          '1-148 To Mana (Based On Character Level)',
          'Damage Reduced By 10%', 
          '50% Better Chance of Getting Magic Items',
          '+2 To All Attributes'
        ],
      }
    },

    armor () {
      return {
        'img': 'eq-archon-plate',
        'width': 75,
        'title': 'Enigma Runeword',
        'runes': 'Jah + Ith + Ber',
        'detail': [
          '+2 To All Skills', 
          '+45% Faster Run/Walk',
          '+1 To Teleport',
          '+750-775 Defense', 
          '0-74 To Strength (Based On Character Level)',
          'Increase Maximum Life 5%',
          'Damage Reduced By 8%',
          '+14 Life After Each Kill',
          '15% Damage Taken Goes To Mana',
          '+1-99% Better Chance of Getting Magic Items (Based On Character Level)'
        ],
      }
    },

    gloves () {
      return {
        'img': 'eq-trang-oulsclaws',
        'width': 70,
        'title': "Trang-Oul's Claws",
        'detail': [
          'Defense: 67-74', 
          '+20% Faster Cast Rate',
          'Cold Resist +30%',
          '+30 Defense', 
          '+25% Poison Skill Damage',
          '+2 To Curses (Necromancer Only)',
        ],
      }
    },

    belt () {
      return {
        'img': 'eq-arachnid-mesh',
        'width': 75,
        'title': 'Arachnid Mesh',
        'detail': [
          'Defense: 119-138', 
          '+90-120% Enhanced Defense',
          'Slows Target By 10%',
          '+1 To All Skills', 
          '+20% Faster Cast Rate',
          'Increases Maximum Mana 5%',
          'Level 3 Venom (11 Charges)',
        ],
      }
    },
  }
}
</script>
